import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import { deriveRunbookRefAlias } from './derive-alias'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'

export interface RunbookRefMentionItem {
  /**
   * Canonical, fully-qualified ref written to the document model — e.g.
   * `@step.detect_intent.channelId`. This is what gets persisted; the menu
   * derives the friendly display alias from it via `deriveRunbookRefAlias`.
   */
  ref: string
  /**
   * Optional override for the display alias. When omitted the menu falls
   * back to the alias derived from `ref` — consumers usually don't need
   * to set this.
   */
  alias?: string
  /**
   * Optional secondary label rendered after the alias when alias collisions
   * make the friendly name alone ambiguous (e.g. step title). Set this on
   * step-output items whose alias collides with another step's output.
   */
  disambiguator?: string
  /** Search keywords (joined alias, label, description) — used for filtering. */
  label: string
  description?: string
  scope: 'params' | 'step' | 'run' | 'env'
  /**
   * `'create'` marks a synthetic item that creates a new runbook
   * parameter on selection (in addition to inserting the ref). Defaults
   * to `'ref'` for normal mention items.
   */
  kind?: 'ref' | 'create'
}

export interface RunbookRefMentionMenuListProps {
  items: RunbookRefMentionItem[]
  command: (item: RunbookRefMentionItem) => void
}

export const RunbookRefMentionMenuList = forwardRef<
  { onKeyDown: (props: { event: KeyboardEvent }) => boolean },
  RunbookRefMentionMenuListProps
>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0)

  // Group by scope. Create-parameter items live inside the `params`
  // group (rendered after existing params) so authors find them in the
  // same "Runbook params" block they'd already scanned for the name.
  // The grouping reorders items vs. `props.items`, so we compute the
  // flat rendered-order list here and use it as the source of truth for
  // both keyboard navigation and Enter — otherwise pressing Enter would
  // commit a different item than the one visually highlighted (the
  // "Create parameter" row would shift the indices).
  const { orderedGroups, orderedItems } = useMemo(() => {
    const groups = new Map<
      RunbookRefMentionItem['scope'],
      RunbookRefMentionItem[]
    >()
    for (const item of props.items) {
      const list = groups.get(item.scope) ?? []
      list.push(item)
      groups.set(item.scope, list)
    }

    // Within each group, push create rows to the end so existing refs
    // appear first and "+ Create parameter" sits as the trailing option.
    for (const list of groups.values()) {
      list.sort((a, b) => {
        const aCreate = a.kind === 'create' ? 1 : 0
        const bCreate = b.kind === 'create' ? 1 : 0
        return aCreate - bCreate
      })
    }

    const groupEntries = Array.from(groups.entries())
    const flat = groupEntries.flatMap(([, items]) => items)

    return { orderedGroups: groupEntries, orderedItems: flat }
  }, [props.items])

  useEffect(() => {
    setSelectedIndex(0)
  }, [props.items])

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      const total = orderedItems.length
      if (total === 0) return false

      if (event.key === 'ArrowUp') {
        setSelectedIndex((current) => (current + total - 1) % total)
        return true
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((current) => (current + 1) % total)
        return true
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        const item = orderedItems[selectedIndex]
        if (item) {
          props.command(item)
          return true
        }
      }
      return false
    },
  }))

  if (orderedItems.length === 0) {
    return (
      <div className='w-[280px] overflow-hidden rounded-sm border border-border bg-card shadow-[0_8px_24px_rgba(13,13,13,0.1)]'>
        <div className='px-3 py-2 text-[12px] text-text-subtle'>
          No matching refs
        </div>
      </div>
    )
  }

  let renderIndex = 0

  return (
    <div className='w-[280px] overflow-hidden rounded-sm border border-border bg-card shadow-[0_8px_24px_rgba(13,13,13,0.1)] font-["Inter",system-ui,sans-serif]'>
      <ScrollArea
        type='always'
        className='max-h-[320px]'
        viewClassName='max-h-[320px] [&>div]:!block'
      >
        {orderedGroups.map(([key, items]) => (
          <div key={key}>
            <div className='border-b border-border bg-surface-subtle px-[10px] py-[7px] text-[10px] font-semibold uppercase tracking-[0.07em] text-text-placeholder'>
              {key === 'params'
                ? 'Procedure params'
                : key === 'step'
                  ? 'Output variables'
                  : key === 'run'
                    ? 'Run meta'
                    : 'Environment'}
            </div>
            {items.map((item) => {
              const idx = renderIndex++
              const selected = idx === selectedIndex
              const isCreate = item.kind === 'create'
              // Create rows source their alias straight from the user's
              // typed query (item.alias). When empty we omit the
              // `@<name>` portion entirely instead of falling back to
              // `deriveRunbookRefAlias`, which would surface internal
              // sentinels like `__new__`.
              const alias = isCreate
                ? (item.alias ?? '').trim()
                : item.alias || deriveRunbookRefAlias(item.ref)
              return (
                <button
                  key={`${item.kind ?? 'ref'}:${item.ref}`}
                  type='button'
                  onClick={() => props.command(item)}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`block w-full text-left px-[10px] py-[8px] ${
                    selected ? 'bg-[#EDF1FD]' : ''
                  }`}
                >
                  <div className='flex items-baseline gap-2 font-mono text-[11.5px]'>
                    {isCreate ? (
                      <span className='text-cobalt-strong'>
                        <span className='mr-1'>+</span>
                        Create parameter
                        {alias && (
                          <>
                            {' '}
                            <span className='font-semibold'>@{alias}</span>
                          </>
                        )}
                        {!alias && '…'}
                      </span>
                    ) : (
                      <span className='text-cobalt-strong'>@{alias}</span>
                    )}
                    {item.disambiguator && (
                      <span className='text-[11px] text-text-placeholder'>
                        · {item.disambiguator}
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <div className='text-[11px] leading-tight text-text-subtle'>
                      {item.description}
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        ))}
      </ScrollArea>
    </div>
  )
})
RunbookRefMentionMenuList.displayName = 'RunbookRefMentionMenuList'
